import { useEffect, useState } from 'react'
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../utils/constants'

const field =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/20'

/**
 * Public TPRM governance landing for intake Approve (redirect target) and Decline (form).
 * Avoids edge-function inline HTML, which some clients showed as raw source.
 */
export function RiskIntakeDecisionPage() {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token') || ''
  const actionParam = (params.get('action') || '').toLowerCase()
  const statusParam = (params.get('status') || '').toLowerCase()
  const supplierParam = params.get('supplier') || ''
  const alreadyParam = params.get('already') === '1'

  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState<{
    status: 'approved' | 'declined'
    supplier: string
    already?: boolean
  } | null>(null)

  useEffect(() => {
    if (statusParam === 'approved' || statusParam === 'declined') {
      setDone({
        status: statusParam,
        supplier: supplierParam || 'Supplier',
        already: alreadyParam,
      })
    }
  }, [statusParam, supplierParam, alreadyParam])

  async function onDecline(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!token) {
      setError('Missing approval token.')
      return
    }
    if (!reason.trim()) {
      setError('Decline reason is required.')
      return
    }
    setBusy(true)
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/supplier-risk-intake-approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          token,
          action: 'reject',
          rejectionReason: reason.trim(),
          json: true,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Decline failed')
      setDone({
        status: 'declined',
        supplier: supplierParam || data.supplierName || 'Supplier',
        already: Boolean(data.already),
      })
      const qs = new URLSearchParams({
        status: 'declined',
        supplier: supplierParam || data.supplierName || 'Supplier',
      })
      if (data.already) qs.set('already', '1')
      window.history.replaceState({}, '', `/risk-intake-decision?${qs}`)
    } catch (err: any) {
      setError(err.message || 'Decline failed')
    } finally {
      setBusy(false)
    }
  }

  const showDeclineForm = !done && (actionParam === 'reject' || actionParam === 'decline') && token

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-lg mx-auto px-4 py-10">
        <div className="flex items-center gap-4 mb-8">
          <img
            src="/assets/logo1.png"
            alt="The Simple"
            className="h-10 max-w-[140px] object-contain"
            onError={(e) => {
              ;(e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        </div>

        <h1 className="text-2xl font-semibold">TPRM governance</h1>
        <p className="text-sm text-slate-600 mt-1">Supplier risk assessment decision</p>

        {done && (
          <div
            className={`mt-8 rounded-lg border px-4 py-4 ${
              done.status === 'approved'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-red-200 bg-red-50 text-red-900'
            }`}
          >
            <p className="text-lg font-semibold">
              {done.already
                ? `Already ${done.status}`
                : done.status === 'approved'
                  ? 'Approved'
                  : 'Declined'}
            </p>
            <p className="mt-2 text-sm">
              The supplier risk assessment request for <strong>{done.supplier}</strong> has been{' '}
              <strong>{done.status}</strong>
              {done.already ? ' previously' : ''}. TPRM governance and the requester have been
              notified.
            </p>
          </div>
        )}

        {showDeclineForm && (
          <form onSubmit={onDecline} className="mt-8 space-y-4 rounded-lg border border-slate-200 bg-white p-5">
            <p className="text-sm">
              Decline supplier risk assessment request
              {supplierParam ? (
                <>
                  {' '}
                  for <strong>{supplierParam}</strong>
                </>
              ) : null}
              .
            </p>
            <label className="block text-sm">
              <span className="font-medium">Reason for decline *</span>
              <textarea
                className={`${field} mt-1 min-h-[100px]`}
                required
                maxLength={2000}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explain why TPRM governance is declining this request…"
              />
            </label>
            {error && (
              <p className="text-sm text-red-700" role="alert">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              className="rounded-md bg-red-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busy ? 'Submitting…' : 'Confirm decline'}
            </button>
            <p className="text-xs text-slate-500">
              The requestor will receive this reason by email. Approve/Decline links are for TPRM
              Governance only.
            </p>
          </form>
        )}

        {!done && !showDeclineForm && (
          <p className="mt-8 text-sm text-slate-600">
            {error ||
              'Open this page from an Approve or Decline link in a TPRM governance email.'}
          </p>
        )}

        {error && showDeclineForm ? null : error && !done ? (
          <p className="mt-4 text-sm text-red-700">{error}</p>
        ) : null}
      </div>
    </div>
  )
}
